import {Injectable} from '@angular/core';
import { BehaviorSubject, combineLatest, from, Observable, Subject, timer } from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {WebsocketService} from '../../shared/websocket/websocket.service';
import {SearchResult, SearchService} from '../../shared/search.service';
import {environment} from '../../../environments/environment';
import {indexOf} from 'lodash-es';
import { addHours, subHours } from 'date-fns/esm';
import {TranslateService} from '@ngx-translate/core';
import {_, parseToDate} from '../../shared/utils';
import {KonegisCauseCode, LiviCauseCode, LiviDetailedCauseCode,
  LiviThirdCauseCode, BundledCauseCode, formatLiviCauseCode} from './common/konegis-cause-code';
import {TrainFidelity} from './common/train-fidelity';
import {RunningTrain, ScheduleTrain, TrainComposition, TrainDependency,
  TrainHistoryEntry, TrainTracking, BaseTrain, TrainShift,
  JourneySection, CompositionLocomotive, CompositionWagon, TrainSubdependency,
  PersonnelShift, TrainEquipment, EquipmentTrain} from './common/train';
import {TrainWebsocketMessage} from '../../shared/websocket/train-websocket.message';
import {PlusCode, Station} from './common/station';
import {MapElement} from '../../shared/map/map-element.model';
import {Point} from 'geojson';
import {parseVAK} from './utils/vak.parser';
import {StateService} from '../../shared/state/state.service';
import {AbstractDataService} from '../../shared/abstract-data.service';
import {ServiceAnswer} from '../../shared/service-answer';
import {AbstractDataModel} from '../../shared/abstract-data.model';
import {AlarmMessage} from '../../alarm/alarm-message';
import {AlarmService} from '../../alarm/alarm.service';
import {FlagService} from '../../shared/flag/flag.service';
import {IFlag} from '../../shared/flag/flag.model';
import {Layer} from '../../shared/map/layer/layer.model';
import {parseTrainHistory} from './utils/train.history.utils';
import * as Comlink from 'comlink';

import { MapService } from '../../shared/map/map.service';
import { filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { BackendAlarm } from '../../alarm/alarm-definitions/backend-alarm';

interface ComlinkExposed {
  getTrainById(id: string): ScheduleTrain;
  getCompositionById(id: string): TrainComposition;
  getRouteById(id: string): string[];
  getSectionById(id: string): unknown;
  getSectionLocationById(id: string): unknown;
}

/**
 * This service is used to interact with VR Group specific backend resources, mostly this uses rego-backend proxy
 * to send requests to VR Konegis API.
 */
@Injectable()
export class VRGroupService extends AbstractDataService<RunningTrain | ScheduleTrain | TrainTracking | TrainDependency> {
  /**
   * Private observables
   */
  private runningTrainStream = new BehaviorSubject<RunningTrain[]>([]);
  private equipmentDataStream = new BehaviorSubject<TrainWebsocketMessage>(new TrainWebsocketMessage());
  private trainCompositionStream = new Subject<TrainComposition>();
  private scheduleTrainDataBS = new BehaviorSubject<ScheduleTrain[]>([]);
  private trainTrackingStream = new Subject<TrainTracking>();
  private trainScheduleStream = new Subject<ScheduleTrain>();
  private trackSectionLocation = new Subject<{ section: string, location: number[] }>();
  private trainFidelity = new BehaviorSubject<TrainFidelity[]>([]);
  private causes = new BehaviorSubject<Map<string, number>>(new Map<string, number>());
  private scheduleTrainWebsocketData$: Observable<ScheduleTrain[]> = this.websocket.newWebsocketMessage$
    .pipe(
      filter(data => data.target === 'train-schedules'),
      map(data => Array.isArray(data.body) ? data.body : [data.body])
    );

  /**
   * Internal maps and references
   */
  private liviCauseCodeList: LiviCauseCode[] = [];
  private worker: Worker;
  private comlink: Comlink.Remote<ComlinkExposed>;
  private searchIncludeArr: string[] = ['tripNumber'];
  private vakMap: Map<number, string>;
  private trainList: RunningTrain[] = [];
  private trainLayers: Map<string, Layer> = new Map();
  private alarms: AlarmMessage[] = [];
  private flags: IFlag[] = [];
  private plusCodes: Map<string, PlusCode>;

  /**
   * Provided Observables
   */
  public trainFidelity$ = this.trainFidelity.asObservable();
  public equipmentTrains: string[] = [];
  public runningTrainData$ = this.runningTrainStream.asObservable();
  public equipmentData$ = this.equipmentDataStream.asObservable();
  public trainSchedule$ = this.trainScheduleStream.asObservable();
  public trainComposition$ = this.trainCompositionStream.asObservable();
  public trainTracking$ = this.trainTrackingStream.asObservable();
  public trackSectionLocation$ = this.trackSectionLocation.asObservable();
  public causes$ = this.causes.asObservable();
  public scheduledTrainData$ = combineLatest([
    this.scheduleTrainDataBS,
    this.alarmService.alarmsActiveForUser$,
    this.flagService.flags$
  ]).pipe(
    map(([trains, alarms, flags]: [ScheduleTrain[], AlarmMessage<BackendAlarm>[], IFlag[]]): ScheduleTrain[] => {
      return trains
        .filter(train => !(train.state === 'RUNNING' || train.state === 'ARRIVED'))
        .filter(train => !!train.category)
        .map(train => {
          return {
            ...train,
            arrTime: parseToDate(train.arrTime),
            depTime: parseToDate(train.depTime),
            createdAt: parseToDate(train.createdAt),
            arrived: train.arrived ? parseToDate(train.arrived) : null
          }
        })
        .map<ScheduleTrain>((train) => {
          if (train.state === 'CANCELLED') {
            return {
              ...train,
              status: 'cancelled'
            };
          }
          return train;
        })
        .map<ScheduleTrain>((train) => {
          return {
            ...this.addDataToBaseTrain<ScheduleTrain>(train, alarms || [], flags || []),
            causes: this.getBundledCauseForTrain(train.timetable)
          };
        });
    }),
    tap(data => console.warn('Piped schedule trains', data)),
  );

  /**
   * Default constructor
   * @param {HttpClient} http - Angular Http Client
   * @param websocket
   * @param state
   * @param alarmService
   * @param flagService
   * @param searchService
   * @param translate
   */
  constructor(protected http: HttpClient,
              private websocket: WebsocketService,
              private state: StateService,
              private alarmService: AlarmService,
              private flagService: FlagService,
              private searchService: SearchService,
              private mapService: MapService,
              private translate: TranslateService) {
    super(http, environment.dataApiURL);
  }

  public async init() {
    const allScheduleTrains$ = timer(0, 1000 * 60 * 10)
      .pipe(
        map(() => from(this.listTrainSchedules())),
        mergeMap(sa => sa),
        filter(sa => sa.isOk),
        map(sa => sa.data.data)
      );

    combineLatest([allScheduleTrains$, this.runningTrainData$])
      .subscribe(([allScheduleTrains, runningTrains]) => {
        if (!!allScheduleTrains && !!runningTrains) {
          /** Scheduled trains with running trains fitered out */
          const filteredTrains = allScheduleTrains.filter(st => !runningTrains.some(rt => rt._id === st._id));
          this.scheduleTrainDataBS.next(filteredTrains);
        }
      });

    this.runningTrainData$
      .subscribe((runningTrains) => {
        /** Filter out running trains */
        const prevTrains = this.scheduleTrainDataBS.getValue();
        const filteredTrains = prevTrains.filter(st => !runningTrains.some(rt => rt._id === st._id));
        this.scheduleTrainDataBS.next(filteredTrains);
      });

    // Updated train schedules from websocket
    this.scheduleTrainWebsocketData$
      .subscribe(newSchedules => {
        const prevSchedules = this.scheduleTrainDataBS.getValue();
        const patchedSchedules = prevSchedules.map(scheduleTrain => {
          const patchedSchedule = newSchedules.find(newSchedule => newSchedule._id === scheduleTrain._id);
          return !!patchedSchedule ? patchedSchedule : scheduleTrain;
        });
        this.scheduleTrainDataBS.next(patchedSchedules);
      });
    this.mapService.layers$.subscribe(layers => {
      layers.forEach(layer => {
        this.trainLayers.set(layer.id, layer);
      });
    })
    this.flagService.flags$.subscribe(flags => {
      console.log('Flags changed!');
      console.log(flags);
      this.flags = flags;
      this.handleRunningTrains(this.trainList);
    });
    this.alarmService.alarmsActiveForUser$.subscribe((data: AlarmMessage[]) => {
      this.alarms = data;
      this.handleRunningTrains(this.trainList);
    });
    this.searchService.searchTermChanged$.subscribe(query => {
      this.worker.postMessage({command: 'SEARCH', searchTerm: query});
    });
    this.websocket.newWebsocketMessage$.subscribe(async (data: TrainWebsocketMessage) => {
      if (data.target === 'trains') {
        this.handleRunningTrains(data.body);
      } else if (data.target === 'train-fidelity') {
        this.calculateFidelity(data.body);
      } else if (data.target === 'locomotives') { // THIS MEANS EQUIPMENT
        const layers = this.state.getPartOfState('mapState').layers;
        if (layers && layers.includes('Junat')) {
          this.equipmentDataStream.next(this.checkIfTrainsEquipment(data));
        } else {
          this.equipmentDataStream.next(data);
        }
      } else if (data.target === 'train-history') {
        const result = parseTrainHistory(data.body, this.translate);
        this.trainFidelity.next(result.fidelity);
        this.causes.next(result.causes);
      }
    });
    this.loadLiviCodes();
    this.worker = new Worker('./vrgroup.worker', {
      type: 'module',
      name: 'vrgroup',
    });
    this.comlink = Comlink.wrap(this.worker);
    this.worker.postMessage({command: 'START'});
    this.worker.addEventListener('message', (msg) => {
      if (!msg || !msg.data) {
        return;
      }
      if (msg.data.type === 'SEARCH') {
        console.log('Worker search found ', msg.data.results);
        const trainListIDs = new Set(this.trainList.map(train => train._id));
        const results: SearchResult[] = (msg.data.results || [])
          .filter(result => !trainListIDs.has(result._id))
          .map((result): SearchResult => {
            const layer = this.getTrainLayer(result);
            return {
              sourceId: 'map_' + layer.id,
              sourceName: layer.name,
              matchType: 'startsWith',
              matchedValue: result.label,
              layerPriority: layer.searchOptions.priority,
              propertyPriority: -1,
              reason: _('not running'),
              feature: new MapElement<Point>(result._id, layer, result, result.type, {
                type: 'Point',
                coordinates: [0, 0]
              })
            };
          });
        this.searchService.publishResult({
          sourceId: 'Non-running trains',
          sourceName: _('Non-running trains'),
          auxiliary: true,
          results,
        });
      } else if (msg.data.type === 'COMPOSITION') {
        // console.log('Composition received', msg.data.compositions);
        this.trainCompositionStream.next(msg.data.compositions);
      } else if (msg.data.type === 'TRAIN-TRACKING') {
        this.trainTrackingStream.next(msg.data);
        // Do something with train tracking messages, if that is desired
      } else if (msg.data.type === 'TRAIN-SCHEDULE') {
        this.trainScheduleStream.next(msg.data.data);
        // Do something with train tracking messages, if that is desired
      } else if (msg.data.type === 'TRACK-SECTION-LOCATION') {
        console.log('TRACK-SECTION-LOCATION received', msg.data);
        this.trackSectionLocation.next(msg.data.data);
      }
      // console.log('WORKER:', msg);
      // this.worker.terminate();
      // this.worker = undefined;
    });
    parseVAK().then(result => {
      this.vakMap = result;
    });

    this.listRunningTrains().then(result => {
      if (result.isOk) {
        this.handleRunningTrains(result.data.data);
      }
    });
  }

  public getVAKLinkForId(id: number) {
    return this.vakMap.get(id);
  }

  public getStationList(): Promise<Station[]> {
    return this.http.get<Station[]>('https://rata.digitraffic.fi/api/v1/metadata/stations').toPromise();
  }

  public async getPlusCodes(): Promise<Map<string, PlusCode>> {
    if (!this.plusCodes) {
      const stations = await this.getStationList();
      const data = await this.http.get<{
        [key: string]: {
          name: string,
          stations: string[],
        }
      }>('/assets/data/vr/plus-codes.json').toPromise();
      this.plusCodes = new Map(
        Object.entries(data)
          // expand station codes into full `Station` objects
          .map(([code, info]) => ([
            code.toUpperCase(),
            {
              code: code.toUpperCase(),
              name: info.name,
              stations: info.stations
                .map(stationCode => stations.find(station => station.stationShortCode.toUpperCase() === stationCode.toUpperCase()))
                .filter(x => x),
            }
          ]))
      );
    }
    return this.plusCodes;
  }

  /**
   * Search current trains for given query string
   * @param {string} query
   * @returns {Map<string, Train>}
   */
  public doSearch(query: string): Map<string, RunningTrain> {
    query = query.toLowerCase();
    const results = new Map<string, RunningTrain>();
    for (const row of this.trainList) {
      console.log(row);
      if (row.tripNumber === 11542) {
      }
      for (const entry of Object.entries(row)) {
        if (!this.searchIncludeArr.includes(entry[0])) {
          continue;
        }
        if (typeof entry[1] === 'string') {
          if (entry[1].toLowerCase().includes(query)) {
            results.set(row._id, row);
          }
        } else if (typeof entry[1] === 'number') {
          if ((entry[1] + '').includes(query)) {
            results.set(row._id, row);
          }
        } else {
          if (entry[1] === query) {
            results.set(row._id, row);
          }
        }
      }
    }
    return results;
  }

  // TODO: service shouldn't provide text formatting
  public getTextForLiviStatus(cause: KonegisCauseCode): string {
    const code = this.chooseAppropriateKonegisCauseCode(cause);
    const liviCauseCode = this.findLiviCauseCode(code);
    return formatLiviCauseCode(code, liviCauseCode);
  }

  private chooseAppropriateKonegisCauseCode(cause: KonegisCauseCode) {
    let code: string;
    if (cause.thirdCategoryCode) {
      code = cause.thirdCategoryCode;
    } else if (cause.detailedCategoryCode) {
      code = cause.detailedCategoryCode;
    } else {
      code = cause.categoryCode;
    }
    return code;
  }

  public findLiviCauseCode(code: string) {
    return this.liviCauseCodeList.find(o => {
      return o.categoryCode === code;
    });
  }

  private handleRunningTrains(data: RunningTrain[]) {
    // console.log(data.length);
    this.trainList = data.filter(train => {
      // console.log(train.state);
      return train.state === 'RUNNING';
    }).map((train: RunningTrain) => {
      this.mutateDataToRunningTrain(train);
      return train;
    });
    // console.log(this.trainList);
    // console.log(this.trainList.length);
    this.runningTrainStream.next(this.trainList);
    this.filterTrainsWithEquipment(this.trainList);
  }

  /**
   * Handler for websocket messages. If websocket contains map data, draw data to map.
   * @param {WebsocketMessage} data
   */
  private calculateFidelity(data: any) {
    // console.log(data);
    const result: TrainFidelity[] = [
      {
        value: data.LONGDISTANCE.value,
        sum: data.LONGDISTANCE.sum,
        count: data.LONGDISTANCE.count,
        id: 'LONGDISTANCE'
      },
      {
        value: data.COMMUTER.value,
        sum: data.COMMUTER.sum,
        count: data.COMMUTER.count,
        id: 'COMMUTER'
      },
      {
        value: data.FREIGHT.value,
        sum: data.FREIGHT.sum,
        count: data.FREIGHT.count,
        id: 'FREIGHT'
      }
    ];
    this.trainFidelity.next(result);
  }

  private loadLiviCodes() {
    console.log('livi train list load codes');
    this.http.get<LiviCauseCode[]>('https://rata.digitraffic.fi/api/v1/metadata/cause-category-codes').subscribe(data => {
      data.forEach(o => {
        this.liviCauseCodeList.push(o);
      });
    });
    this.http.get<LiviDetailedCauseCode[]>('https://rata.digitraffic.fi/api/v1/metadata/detailed-cause-category-codes').subscribe(data => {
      data.forEach(o => {
        this.liviCauseCodeList.push({
          id: o.id,
          categoryCode: o.detailedCategoryCode,
          categoryName: o.detailedCategoryName,
          validFrom: o.validFrom
        });
      });
    });
    this.http.get<LiviThirdCauseCode[]>('https://rata.digitraffic.fi/api/v1/metadata/third-cause-category-codes').subscribe(data => {
      data.forEach(o => {
        this.liviCauseCodeList.push({
          id: o.id,
          categoryCode: o.thirdCategoryCode,
          categoryName: o.thirdCategoryName,
          validFrom: o.validFrom
        });
      });
    });
  }

  public async getTrainMapElementById(id: string): Promise<MapElement<Point, RunningTrain | ScheduleTrain> | null> {
    const marker = this.mapService.getMarker(id);
    if (marker) {
      return marker;
    }

    const train = await this.getTrainById(id);
    if (!train) {
      return null;
    }

    this.mutateDataToBaseTrain(train);
    return new MapElement<Point>(train._id, train.layer, train, train.type, null);
  }

  public async getTrainMapElement(tripNumber: string | number, tripDate: string): Promise<MapElement<Point, RunningTrain | ScheduleTrain> | null> {
    const id = `${tripDate}_${tripNumber}`;
    return this.getTrainMapElementById(id);
  }

  public getTrainById(id: string): Promise<ScheduleTrain> {
    return this.comlink.getTrainById(id);
  }

  public getCompositionById(id: string): Promise<TrainComposition> {
    return this.comlink.getCompositionById(id);
  }

  public getRouteById(id: string): Promise<string[]> {
    return this.comlink.getRouteById(id);
  }

  public makeCompositionRequest(id: string, station: string): void {
    if (this.worker) {
      this.worker.postMessage({command: 'GET_COMPOSITION', trainId: id, station: station});
    }
  }

  public makeScheduleRequest(id: string): void {
    if (this.worker) {
      this.worker.postMessage({command: 'GET_SCHEDULE', trainId: id});
    }
  }

  public makeTrackSectionLocationRequest(id: string): void {
    if (this.worker) {
      this.worker.postMessage({command: 'GET_POSITION_FOR_TRACK_SECTION', sectionId: id});
    }
  }

  private listRunningTrains(): Promise<ServiceAnswer<AbstractDataModel<RunningTrain>>> {
    return super.filterData<RunningTrain>('train-locations?state="RUNNING"');
  }

  private listTrainSchedules(): Promise<ServiceAnswer<AbstractDataModel<ScheduleTrain>>> {
    return super.filterData<ScheduleTrain>('train-schedules?state!="ARRIVED"&state!= "RUNNING"&depTime>' +
      subHours(new Date(), 4).toJSON() + '&depTime<' + addHours(new Date(), 24).toJSON() + '&sort=+depTime');
  }

  public getTrainSchedule(id: string): Promise<ServiceAnswer<AbstractDataModel<ScheduleTrain>>> {
    return super.filterData<ScheduleTrain>('train-schedules?_id=' + id);
  }

  public async getTripDatesByTripNumber(tripNumber: number): Promise<string[]> {
    const data = await super.filterData<Partial<ScheduleTrain>>(`train-schedules?fields=tripDate&tripNumber="${tripNumber}"`);
    return data.data?.data?.map(train => train.tripDate) || [];
  }

  public getTrainData(id: string): Promise<ServiceAnswer<AbstractDataModel<RunningTrain>>> {
    return super.filterData<RunningTrain>('train-locations?_id=' + id);
  }

  public getTrainTracking(id: string): Promise<ServiceAnswer<AbstractDataModel<TrainTracking>>> {
    return super.filterData<TrainTracking>('train-tracking?trainId=' + id);
  }

  public getlastTrainTracking(id: string): Promise<ServiceAnswer<AbstractDataModel<TrainTracking>>> {
    return super.filterData<TrainTracking>('train-tracking?trainId=' + id + '&limit=1&sort=-timestamp');
  }

  public getTrainsForStation(station: string, tripDate: string): Promise<ServiceAnswer<AbstractDataModel<ScheduleTrain>>> {
    return super.filterData<ScheduleTrain>('train-schedules?timetable.station=' + station + '&tripDate=' + tripDate + '&sort=+depTime')
      .then(serviceAnswer => {
        if (serviceAnswer.isOk) {
          // Add data to response (addDataToScheduleTrain mutates the train)
          serviceAnswer.data.data.forEach(train => this.mutateDataToScheduleTrain(train));
        }
        return serviceAnswer;
      });
  }

  public async getTrainDependencies(
    tripNumber: string,
    tripDate: string): Promise<ServiceAnswer<{ dependentTrains: TrainDependency[], dependeeTrains: TrainDependency[] }>> {
    const serviceAnswer: ServiceAnswer<{ dependentTrains: TrainDependency[], dependeeTrains: TrainDependency[] }> = {isOk: null, isDataEqual: true};
    const query = `${environment.regoApiURL}traindependencies/?trainId=${tripNumber}&departureDate=${tripDate}`;
    try {
      serviceAnswer.data = await this.http.get<{ dependentTrains: TrainDependency[], dependeeTrains: TrainDependency[] }>(query).toPromise();
      serviceAnswer.isOk = true;
    } catch (error) {
      console.error(`Error when fetching entities from ${this.url + query}: ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
    }
    return serviceAnswer;
  }

  public getFidelityHistory(start, end): Promise<ServiceAnswer<AbstractDataModel<any>>> {
    return super.filterData<any>('train-fidelity?createdAt>' + start.toJSON() + '&createdAt<' + end.toJSON() + '&sort=+createdAt');
  }

  public getTrainHistory(start: Date, end: Date): Promise<ServiceAnswer<AbstractDataModel<TrainHistoryEntry[]>>> {
    return super.filterData<TrainHistoryEntry[]>('train-history?createdAt>' + start.toJSON() + '&createdAt<' + end.toJSON() + '&sort=+createdAt');
  }

  public getMegafilesForTrain(id: string): Promise<ServiceAnswer<AbstractDataModel<any>>> {
    return super.filterData<any>('megafile-entries?trainId=' + id);
  }

  private filterTrainsWithEquipment(data: RunningTrain[]) {
    this.equipmentTrains = [];
    data.forEach((o: any) => {
      if (o.equipment) {
        this.equipmentTrains.push(o.equipment);
      }
    });
  }

  public checkIfTrainsEquipment(data: TrainWebsocketMessage): TrainWebsocketMessage {
    data.body = data.body.filter((o: any) => {
      return indexOf(this.equipmentTrains, o._id) === -1;
    });
    return data;
  }

  private getBundledCauseForTrain(timetableArr: {
      station: string;
      causes: KonegisCauseCode;
    }[]) {
    const causesMap = new Map<string, BundledCauseCode>();
    if (timetableArr && timetableArr.length > 0 && timetableArr[0].causes) {
      const konegisCauseCode = timetableArr[0].causes;
      const determinedCode = this.chooseAppropriateKonegisCauseCode(konegisCauseCode);
      const liviCauseCode = this.findLiviCauseCode(determinedCode);
      const plainTextCode: BundledCauseCode = {
        determinedKonegisCauseCode: determinedCode,
        konegisCauseCode,
        liviCauseCode
      };
      causesMap.set(timetableArr[0].station, plainTextCode);
    }

    return causesMap;
  }

  private mutateDataToScheduleTrain(train: ScheduleTrain) {
    this.mutateDataToBaseTrain(train);
    train.causes = this.getBundledCauseForTrain(train.timetable);
  }

  private mutateDataToRunningTrain(train: RunningTrain) {
    this.mutateDataToBaseTrain(train);
    if (train.timetable) {
      train.causes = this.getBundledCauseForTrain(train.timetable);
    }
  }

  private addDataToBaseTrain<TTrain extends BaseTrain>(
    train: TTrain,
    allAlarms: AlarmMessage<BackendAlarm>[],
    allFlags: IFlag[]
  ): TTrain {
    const trainFlags = allFlags.filter(flag => flag.targetObjectId === train._id);
    return {
      ...train,
      flags: trainFlags,
      flagged: trainFlags.length !== 0,
      alarms: allAlarms.filter(alarm => {
        const trainId = train.tripDate + '_' + train.tripNumber;
        if (alarm.metadata.type === 'vrTrainConflict' || alarm.metadata.type === 'vrShiftConflict') {
          const causingTrainId = alarm.metadata.payload.trainDate + '_' + alarm.metadata.payload.tripNumber;
          const dependentTrainId = alarm.metadata.payload.dependentTrainDate + '_' + alarm.metadata.payload.dependentTripNumber;
          return causingTrainId === trainId || dependentTrainId === trainId;
        }
        // add all other alarm types
        if (alarm.metadata.type !== 'miproMeasure') {
          const alarmTrainId = AlarmService.getIdentifier(alarm.metadata);
          return alarmTrainId === trainId;
        }
        return false;
      }),
      layer: this.getTrainLayer(train)
    }
  }

  private mutateDataToBaseTrain(train: BaseTrain): void {
    train.flags = this.flags.filter((k: any) => {
      return k.targetObjectId === train._id;
    });
    train.flagged = train.flags.length !== 0;
    train.alarms = this.alarms.filter(o => {
      // if (!o.metadata.active) {
      //   return false;
      // }

      const trainId = train.tripDate + '_' + train.tripNumber;
      if (o.metadata.type === 'vrTrainConflict' || o.metadata.type === 'vrShiftConflict') {
        const causingTrainId = o.metadata.payload.trainDate + '_' + o.metadata.payload.tripNumber;
        const dependentTrainId = o.metadata.payload.dependentTrainDate + '_' + o.metadata.payload.dependentTripNumber;
        return causingTrainId === trainId || dependentTrainId === trainId;
      }
      // add all other alarm types
      if (o.metadata.type !== 'miproMeasure') {
        const alarmTrainId = AlarmService.getIdentifier(o.metadata);
        return alarmTrainId === trainId;
      }

      return false;
    });
    train.layer = this.getTrainLayer(train);
  }

  public getTrainLayer(train: BaseTrain): Layer {
    switch (train.type) {
      case 'LONGDISTANCE':
        return this.trainLayers.get('Kaukojunat');
      case 'COMMUTER':
        return this.trainLayers.get('Lähijunat');
      case 'LOCOMOTIVE':
        return this.trainLayers.get('Veturit');
      case 'OTHER':
        return this.trainLayers.get('Muut');
      default:
        if (this.trainLayers.has(train.category)) {
          return this.trainLayers.get(train.category);
        } else {
          console.warn('No layer found for train', train);
          return this.trainLayers.get('Muut');
        }
    }
  }

  public getEquipmentLayer(train: EquipmentTrain): Layer {
    const trainLayerMapKey = train.title.toUpperCase();
    if (this.trainLayers.has(trainLayerMapKey)) {
      return this.trainLayers.get(trainLayerMapKey);
    } else {
      console.error('No layer found for train', train);
    }
  }


  /**
   * Get shifts data by trainid and operatingdate
   * @param trainNumber
   * @param operatingDay
   */
  public async getTrainShift(trainNumber: string, operatingDate: string): Promise<ServiceAnswer<TrainShift>> {
    const serviceAnswer: ServiceAnswer<TrainShift> = {isOk: null, isDataEqual: true};
    const query = `${environment.regoApiURL}shiftdependencies/bytrain?trainId=${trainNumber}&departureDate=${operatingDate}`;
    try {
      serviceAnswer.data = this.recurseDates(JSON.stringify(await this.http.get<TrainShift>(query).toPromise()));
      serviceAnswer.isOk = true;
    } catch (error) {
      console.error(`Error when fetching entities from ${this.url + query}: ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
    }
    return serviceAnswer;
  }
  /**
   * Get shifts data by personid and mindate
   * @param personId
   * @param minDate
   */
  public async getPersonShift(personId: string, minDate: string): Promise<ServiceAnswer<PersonnelShift>> {
    const serviceAnswer: ServiceAnswer<PersonnelShift> = {isOk: null, isDataEqual: true};
    const query = `${environment.regoApiURL}shiftdependencies/byperson?personId=${personId}&minDate=${minDate}`;
    try {
      serviceAnswer.data = this.recurseDates(JSON.stringify(await this.http.get<PersonnelShift>(query).toPromise()));
      serviceAnswer.isOk = true;
    } catch (error) {
      console.error(`Error when fetching entities from ${this.url + query}: ${JSON.stringify(error)}`);
      serviceAnswer.isOk = false;
      serviceAnswer.errorCode = error.status;
      serviceAnswer.errorMessage = error.message + '. Answer body: ' + JSON.stringify(error.error);
    }
    return serviceAnswer;
  }

  /**
   * Renders a JourneySection's composition as a string,
   * taking care of ordering locomotives/wagons.
   */
  public compositionStringFromJourneySection(section: JourneySection): string {
    if (!section) {
      return '';
    }
    // Pendolino is treated as a single unit, despite the API showing multiple wagons
    if (this.isPendolino(section.locomotives.map(loc => loc.locomotiveType))) {
      return 'Sm3';
    }

    // collect locomotives and wagons into a single array and order by location
    return (section.locomotives as Array<CompositionLocomotive | CompositionWagon>)
      .concat(section.wagons || [])
      .sort((a, b) => a.location - b.location)
      .map(comp => (comp as CompositionLocomotive).locomotiveType ?
        (comp as CompositionLocomotive).locomotiveType
        : (comp as CompositionWagon).wagonType)
      .join(' ');
  }

  /**
   * Renders a TrainSubdependency's composition as a string.
   */
  public compositionStringFromTrainSubdependency(subDependencies: TrainSubdependency): {locomotives: string, wagons: string} {
    let locomotives = '';
    let locomotiveEquipmentTypes = [];
    let wagons = '';
    /**Locomotives */
    if (subDependencies.locs) {
      locomotiveEquipmentTypes = subDependencies.locs.map(loc => `${loc.equipmentType} (${loc.locId})`);
      locomotives = this.getLocomotiveEquipmentTypes(locomotiveEquipmentTypes).join('');
    } else if (subDependencies.compositions) {
      locomotiveEquipmentTypes = subDependencies.compositions
        .filter(comp => comp.locId)
        .map(loc => `${loc.equipmentType} (${loc.locId})`);
      locomotives = this.getLocomotiveEquipmentTypes(locomotiveEquipmentTypes).join('');
    }

    /**Wagons */
    if (subDependencies.compositions) {
      wagons = this.compositionWagonsFromTrainSubdependency(subDependencies.compositions).join('');
    }
    return {locomotives, wagons};
  }

  private compositionWagonsFromTrainSubdependency(compositions: TrainEquipment[]) {
    // flatten wagon groups into a 1-dimensional array
    return compositions
      .flatMap(comp => comp.vehicles?.length > 0 ? comp.vehicles : [{type: comp.equipmentType}])
      .sort((a, b) => +b.bookingNumber - +a.bookingNumber)
      .map(vehicle => vehicle.bookingNumber ? `${vehicle.type}<span class="sub">${vehicle.bookingNumber}</span>` : vehicle.type);
  }

  private getLocomotiveEquipmentTypes(locomotiveEquipmentTypes: TrainEquipment['equipmentType'][]) {
    if (this.isPendolino(locomotiveEquipmentTypes)) {
      return ['Sm3'];
    }
    return locomotiveEquipmentTypes;
  }

  private isPendolino(locomotiveEquipmentTypes: string[]) {
    // Pendolino is treated as a single unit, despite the API showing multiple wagons
    return locomotiveEquipmentTypes.length > 0 && locomotiveEquipmentTypes[0].toUpperCase() === 'SM3';
  }

  /**
   * self contained conversion of date strings to date objects
   * strings beginning with YYYY-MM-DDTHH:mm:SS will be converted
   * @param jsonString
   */
  private recurseDates(jsonString: string): any {
    return JSON.parse(jsonString, (key, value) => {
      const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
      if (typeof value === 'string' && dateFormat.test(value)) {
        // console.log('parsing ' + key + ' date:' + value);
        return new Date(value);
      }
      return value;
    });
  }
}
